import type { PropsOf } from '@emotion/react'
import type { FC, MouseEvent } from 'react'
import { useCallback } from 'react'
import { useState } from 'react'
import { BsStars } from 'react-icons/bs'
import { useNavigate } from 'react-router-dom'

import { Button } from '@app/components/ui/button'
import { toaster } from '@app/components/ui/toaster'
import AiPlaybooksModal from '@app/pages/playbooks/aiPlaybooksModal'
import type AiPlaybooksModalAction from '@app/pages/playbooks/components/aiPlaybooksModalAction'
import CardSection from '@app/shared/cards/components/cardSection'
import withSuspenseWrapper from '@app/shared/withSuspenseWrapper'
import type { CardSize } from '@app/types'
import { usePlaybooksQuery, usePlaybookApplyToStrategyWithAiMutation } from '@graphql/queries'

interface Props {
  id: string
  size: CardSize
  parentStrategyId: string
}

const CardStrategyCTAAction: FC<PropsOf<typeof AiPlaybooksModalAction>> = ({
  playbook,
  strategyId,
  parentStrategyId,
  additionalInstructions,
  onLoadingChange = null
}) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const [, updateStrategyWithAi] = usePlaybookApplyToStrategyWithAiMutation()

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.preventDefault()
      setLoading(true)
      onLoadingChange?.(true)

      updateStrategyWithAi({
        input: {
          playbookId: playbook.id,
          strategyId,
          additionalInstructions
        }
      })
        .then((resp) => {
          if (resp.error) {
            throw resp
          }

          const { errors } = resp.data.playbookApplyToStrategyWithAi

          if (errors.length) {
            throw errors
          }

          toaster.create({
            title: 'Updating strategy',
            type: 'success'
          })

          const { strategy, chat } = resp.data.playbookApplyToStrategyWithAi

          navigate(`/strategy/${parentStrategyId}/map/preview/${strategy.id}?preview=true&chatId=${chat.id}`)
        })
        .catch(() => {
          // stay loading in success case
          setLoading(false)
          onLoadingChange?.(false)
        })
    },
    [
      onLoadingChange,
      updateStrategyWithAi,
      playbook?.id,
      strategyId,
      additionalInstructions,
      navigate,
      parentStrategyId
    ]
  )

  return (
    <Button
      colorPalette="purple"
      loading={loading}
      onClick={handleClick}
      data-playbook-name={playbook?.name}
      data-playbook-id={playbook?.id}
      disabled={!playbook}
    >
      <BsStars />
      Build with AI
    </Button>
  )
}

const CardStrategyCTA: FC<Props> = ({ parentStrategyId, id, size, ...rest }) => {
  const [{ data }] = usePlaybooksQuery({ variables: { aiOnly: true } })

  return (
    <AiPlaybooksModal
      parentStrategyId={parentStrategyId}
      showCTA
      playbooks={data.playbooks}
      strategyId={id}
      Action={CardStrategyCTAAction}
    >
      <CardSection size={size} bgColor="blue.50">
        <Button w="full" variant="surface" colorPalette="blue" {...rest}>
          Build map with AI
        </Button>
      </CardSection>
    </AiPlaybooksModal>
  )
}

export default withSuspenseWrapper(CardStrategyCTA)
